// src/pages/supervisor/ViewReport.tsx
import { useCallback, useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";
import { AuthProvider } from "../../context/AuthContext";
import Header from "../../components/supervisor/Header";
import Sidebar from "../../components/supervisor/Sidebar";
import { uploadToCloudinary } from "../../utils/uploadToCloudinary";
import { FaEye, FaFileAlt, FaUser, FaCalendarAlt, FaMapMarkerAlt, FaDownload, FaTrash } from "react-icons/fa";

interface Property {
  id: string;
  upi: string;
  ownerName: string;
  province?: string;
  district?: string;
  sector?: string;
  cell?: string;
  village?: string;
}

interface Reporter {
  id: string;
  name: string;
  email?: string;
  upi?: string;
  role: string;
  type: 'USER' | 'VIEWER';
}

interface Report {
  id: string;
  propertyId: string;
  reportData: {
    reporterEmail: string;
    reporterRole: string;
  };
  pdfUrl: string;
  generatedAt: string;
  generatedBy: string;
  viewerGeneratedBy: string | null;
  generatedByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
  generatedByViewer?: {
    id: string;
    upi: string;
    role: string;
  };
  reporter?: Reporter;
  property?: Property;
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme };
}

function ViewReportContent() {
  const { theme, setTheme } = useTheme();
  const { logout, token: authToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [reports, setReports] = useState<Report[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [propertySearch, setPropertySearch] = useState("");
  const [viewingReport, setViewingReport] = useState<Report | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);

  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  // Calculate statistics
  const totalReports = reports.length;
  const today = new Date().toDateString();
  const reportsToday = reports.filter(report => 
    new Date(report.generatedAt).toDateString() === today
  ).length;
  const uniqueReporters = new Set(
    reports.map(report => report.reporter?.id).filter(Boolean)
  ).size;

  // Format location
  const formatLocation = (property?: Property) => {
    if (!property) return "—";
    const parts = [property.district, property.sector, property.cell, property.village].filter(Boolean);
    return parts.join(", ") || "—";
  };

  // Get reporter name
  const getReporterName = (report: Report) => {
    if (report.generatedByUser) {
      return `${report.generatedByUser.firstName} ${report.generatedByUser.lastName}`;
    }
    if (report.generatedByViewer) {
      return report.generatedByViewer.upi;
    }
    return "Unknown";
  };

  // Get reporter role
  const getReporterRole = (report: Report) => {
    if (report.generatedByUser) {
      return report.generatedByUser.role;
    }
    if (report.generatedByViewer) {
      return report.generatedByViewer.role;
    }
    return "Unknown";
  };

  /* ================= FETCH PROPERTIES ================= */
  useEffect(() => {
    if (!authToken) return;

    (async () => {
      try {
        setLoadingProperties(true);
        const res = await fetch(`${API_BASE_URL}/properties?limit=100`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const result = await res.json();
        setProperties(result?.data?.properties || result?.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingProperties(false);
      }
    })();
  }, [API_BASE_URL, authToken]);

  /* ================= FETCH REPORTS ================= */
  const fetchReports = useCallback(async () => {
    if (!authToken) return;

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/reports?includeReporter=true&includeProperty=true`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const result = await res.json();
      setReports(result?.data?.reports || result?.data || []);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, authToken]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  /* ================= FILTER PROPERTIES ================= */
  const filteredProperties = properties.filter((p) => {
    if (!propertySearch.trim()) return true;
    const term = propertySearch.toLowerCase();
    return (
      p.upi?.toLowerCase().includes(term) ||
      p.ownerName?.toLowerCase().includes(term) ||
      p.district?.toLowerCase().includes(term) ||
      p.province?.toLowerCase().includes(term)
    );
  });

  /* ================= FILE HANDLING ================= */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== "application/pdf") {
      alert("Please select a PDF file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("File size should be less than 10MB");
      return;
    }

    setSelectedFile(file);
  };

  /* ================= CREATE REPORT ================= */
  const handleUpload = async () => {
    if (!authToken) {
      alert("Authentication token missing. Please log in again.");
      return;
    }

    if (!selectedFile || !selectedPropertyId) {
      alert("Please select both PDF file and property");
      return;
    }

    try {
      setUploading(true);
      const pdfUrl = await uploadToCloudinary(selectedFile);

      const payload = {
        propertyId: selectedPropertyId,
        pdfUrl,
      };

      const res = await fetch(`${API_BASE_URL}/reports`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to create report");
      }

      const result = await res.json();
      setReports((prev) => [result.data || result, ...prev]);

      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedPropertyId(null);
      setPropertySearch("");

      alert("Report created successfully!");
    } catch (error: any) {
      console.error("Upload error:", error);
      alert(`Failed to create report: ${error.message || "Unknown error"}`);
    } finally {
      setUploading(false);
    }
  };

  /* ================= VIEW REPORT DETAILS ================= */
  const handleViewReport = (report: Report) => {
    setViewingReport(report);
    setShowDetailsModal(true);
  };

  /* ================= DELETE REPORT ================= */
  const handleDeleteClick = (report: Report) => {
    setReportToDelete(report);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!authToken || !reportToDelete) return;

    try {
      setDeletingReportId(reportToDelete.id);

      const res = await fetch(`${API_BASE_URL}/reports/${reportToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete report");
      }

      // Remove report from state
      setReports((prev) => prev.filter((r) => r.id !== reportToDelete.id));

      setShowDeleteConfirm(false);
      setReportToDelete(null);
      alert("Report deleted successfully!");
    } catch (error: any) {
      console.error("Delete error:", error);
      alert(`Failed to delete report: ${error.message || "Unknown error"}`);
    } finally {
      setDeletingReportId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setReportToDelete(null);
  };

  /* ================= UI ================= */
  if (loading) return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading reports...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <Header theme={theme} setTheme={setTheme} logout={logout} />
      <div className="flex">
        <Sidebar onViewData={() => {}} onViewReport={() => {}} />

        <main className="flex-1 lg:ml-64 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold">Property Valuation Reports</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  Manage and view all property valuation reports
                </p>
              </div>
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow flex items-center gap-2"
              >
                <FaFileAlt className="h-5 w-5" />
                Upload Report
              </button>
            </div>

            {/* ================= STATS CARDS ================= */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <FaFileAlt className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Reports</p>
                    <p className="text-2xl font-bold">{totalReports}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                    <FaCalendarAlt className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Reports Today</p>
                    <p className="text-2xl font-bold">{reportsToday}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                    <FaUser className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Unique Reporters</p>
                    <p className="text-2xl font-bold">{uniqueReporters}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= REPORTS TABLE ================= */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="p-4 font-medium">Property UPI</th>
                    <th className="p-4 font-medium">Property Location</th>
                    <th className="p-4 font-medium">Reporter</th>
                    <th className="p-4 font-medium">Role</th>
                    <th className="p-4 font-medium">Generated At</th>
                    <th className="p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-12 text-center text-gray-500 dark:text-gray-400">
                        <FaFileAlt className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <p className="text-lg">No reports yet</p>
                        <p className="text-sm mt-2">Upload your first property valuation report</p>
                      </td>
                    </tr>
                  ) : (
                    reports.map((report) => (
                      <tr key={report.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="p-4">
                          <div className="font-medium">{report.property?.upi || "—"}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {report.property?.ownerName || "Unknown owner"}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />
                            <span>{formatLocation(report.property)}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="font-medium">{getReporterName(report)}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {report.generatedByUser?.email || report.generatedByViewer?.upi || "—"}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300">
                            {getReporterRole(report)}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            {new Date(report.generatedAt).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(report.generatedAt).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewReport(report)}
                              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg"
                              title="View Report Details"
                            >
                              <FaEye className="h-5 w-5" />
                            </button>
                            {report.pdfUrl && (
                              <a
                                href={report.pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900 rounded-lg"
                                title="Open PDF"
                              >
                                <FaDownload className="h-5 w-5" />
                              </a>
                            )}
                            <button
                              onClick={() => handleDeleteClick(report)}
                              disabled={deletingReportId === report.id}
                              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg disabled:opacity-50"
                              title="Delete Report"
                            >
                              {deletingReportId === report.id ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                              ) : (
                                <FaTrash className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* ================= UPLOAD MODAL ================= */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold">Upload Valuation Report</h2>
              <button
                onClick={() => !uploading && setShowUploadModal(false)}
                disabled={uploading}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
              {/* Property Search */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Select Property <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Search by UPI, owner, district..."
                  value={propertySearch}
                  onChange={(e) => setPropertySearch(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  disabled={uploading}
                />
                <select
                  value={selectedPropertyId || ""}
                  onChange={(e) => setSelectedPropertyId(e.target.value || null)}
                  className="w-full mt-2 px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  disabled={uploading}
                >
                  <option value="">— Select property —</option>
                  {filteredProperties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.upi} • {p.ownerName} • {p.district || p.province || "—"}
                    </option>
                  ))}
                  {filteredProperties.length === 0 && propertySearch && (
                    <option disabled>No matches found</option>
                  )}
                </select>
              </div>

              {/* PDF Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Valuation Report PDF <span className="text-red-500">*</span>
                </label>
                <div
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${
                    uploading
                      ? "opacity-60 cursor-not-allowed border-gray-300 dark:border-gray-600"
                      : selectedFile
                      ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                      : "border-gray-300 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  }`}
                >
                  {selectedFile ? (
                    <div className="space-y-2">
                      <FaFileAlt className="h-12 w-12 text-green-500 mx-auto" />
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      {!uploading && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                          }}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Remove File
                        </button>
                      )}
                    </div>
                  ) : (
                    <div>
                      <FaFileAlt className="h-12 w-12 text-gray-300 dark:text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-300">Click to select PDF valuation report</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Maximum file size: 10MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </div>
              </div>

              {/* Uploading Spinner */}
              {uploading && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
                  <p className="text-blue-600 dark:text-blue-400 font-medium">Uploading report...</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Please wait while we process your file
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t dark:border-gray-700 flex justify-end gap-4">
              <button
                onClick={() => !uploading && setShowUploadModal(false)}
                disabled={uploading}
                className="px-6 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || !selectedPropertyId || uploading}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  "Upload Report"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= VIEW DETAILS MODAL ================= */}
      {showDetailsModal && viewingReport && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold">Report Details</h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setViewingReport(null);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
              {/* Property Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <FaMapMarkerAlt className="h-5 w-5" />
                  Property Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">UPI</p>
                    <p className="font-medium">{viewingReport.property?.upi || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Owner Name</p>
                    <p className="font-medium">{viewingReport.property?.ownerName || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">District</p>
                    <p className="font-medium">{viewingReport.property?.district || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Sector</p>
                    <p className="font-medium">{viewingReport.property?.sector || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Cell</p>
                    <p className="font-medium">{viewingReport.property?.cell || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Village</p>
                    <p className="font-medium">{viewingReport.property?.village || "—"}</p>
                  </div>
                </div>
              </div>

              {/* Report Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <FaFileAlt className="h-5 w-5" />
                  Report Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Report ID</p>
                    <p className="font-medium text-sm">{viewingReport.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Generated Date</p>
                    <p className="font-medium">
                      {new Date(viewingReport.generatedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Generated Time</p>
                    <p className="font-medium">
                      {new Date(viewingReport.generatedAt).toLocaleTimeString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">PDF File</p>
                    {viewingReport.pdfUrl ? (
                      <a
                        href={viewingReport.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium flex items-center gap-2"
                      >
                        <FaDownload className="h-4 w-4" />
                        View PDF
                      </a>
                    ) : (
                      <p className="font-medium">—</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Reporter Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <FaUser className="h-5 w-5" />
                  Reporter Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
                    <p className="font-medium">{getReporterName(viewingReport)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Role</p>
                    <p className="font-medium">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300">
                        {getReporterRole(viewingReport)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Email/UPI</p>
                    <p className="font-medium">
                      {viewingReport.generatedByUser?.email || viewingReport.generatedByViewer?.upi || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Type</p>
                    <p className="font-medium">
                      {viewingReport.generatedByUser ? "User" : viewingReport.generatedByViewer ? "Viewer" : "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setViewingReport(null);
                }}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= DELETE CONFIRMATION MODAL ================= */}
      {showDeleteConfirm && reportToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b dark:border-gray-700">
              <h2 className="text-xl font-bold text-red-600 dark:text-red-400 flex items-center gap-2">
                <FaTrash className="h-5 w-5" />
                Delete Report
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to delete this report? This action cannot be undone.
              </p>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Property UPI:</span>
                  <span className="font-medium">{reportToDelete.property?.upi || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Owner:</span>
                  <span className="font-medium">{reportToDelete.property?.ownerName || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Generated:</span>
                  <span className="font-medium">
                    {new Date(reportToDelete.generatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t dark:border-gray-700 flex justify-end gap-4">
              <button
                onClick={handleDeleteCancel}
                disabled={!!deletingReportId}
                className="px-6 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={!!deletingReportId}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {deletingReportId ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash className="h-4 w-4" />
                    Delete Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ViewReport() {
  return (
    <AuthProvider>
      <ProtectedRoute allowedRoles={["SUPERVISOR", "ADMIN", "INSTITUTIONAL_CLIENT"]}>
        <ViewReportContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}